import { ensureDefined } from "common/utils/assertOfType";
import RBush from "rbush";
import type { AABB2 } from "../aabb2Types";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "../core";
import { CurveCurvePointIntersection, intersectLineSegments2D } from "../intersectLine2Line2";
import { vec2Dist, vec2DistSquared } from "../vec2";
import { buildSnapRoundSegments } from "./buildSnapRoundHalfEdges";
import type { HalfEdge, Point, Polygon, Segment } from "./dcel";
import { DCEL } from "./dcel";
import { ObjectPool } from "./objectPool";
import { pointInPolygon } from "./pointInPolygon";
import { vec2Sub } from "./vec2";

interface SegmentBox extends AABB2 {
  segmentIndex: number;
  length: number;
}

type SegmentIsectParam = {
  t: number;
  pt: Point;
  vertexIndex: number;
};

/**
 * We reuse the segment RTree across calls
 */
const segmentRTree = new RBush<SegmentBox>();

const segmentIsectParamPool = new ObjectPool<SegmentIsectParam>(() => ({
  t: 0,
  pt: [0, 0],
  vertexIndex: -1,
}));

const bboxPool = new ObjectPool<SegmentBox>(() => ({
  segmentIndex: 0,
  minX: -0,
  minY: -0,
  maxX: -0,
  maxY: -0,
  length: 0,
}));

const segmentPool = new ObjectPool<Segment>(() => [-0, -0, -0, -0]);

export const DEFAULT_SNAP_ROUND_PRECISION = 1e-6;

export interface DCELFromPolygonsParams {
  polygons: Polygon[];
  segments?: Segment[];
  segmentPolygonIds?: number[];
  snapRoundPrecision?: number;
  preventPolygonSelfOverlaps?: boolean;
  constraintSegments?: Segment[];
  constraintPts?: Point[];
}

export function dcelFromPolygonsSnapRound(params: DCELFromPolygonsParams & { snapRoundPrecision: number }): DCEL {
  return dcelFromPolygons(params);
}

export function dcelFromPolygons(params: DCELFromPolygonsParams): DCEL {
  const {
    polygons,
    segments = [],
    segmentPolygonIds = [],
    snapRoundPrecision,
    preventPolygonSelfOverlaps = true,
    constraintSegments = [],
    constraintPts = [],
  } = params;
  bboxPool.release();
  segmentPool.release();
  segmentIsectParamPool.release();

  const {
    segments: allSegments,
    segmentToPolygon,
    segmentToPolygonLoop,
    segmentToPolygonLoopEdge,
  } = decomposeIntoSegments(polygons);

  if (segments.length) {
    for (let i = 0; i < segments.length; i++) {
      allSegments.push(segments[i]);
      segmentToPolygon.push(segmentPolygonIds[i]);
      segmentToPolygonLoop.push(0); // there is only one loop per segment - treat it as the loop index
      segmentToPolygonLoopEdge.push(0); // there is only one edge per segment - treat it as the edge index
    }
  }

  const updatedIntsPts: Point[] = [
    ...constraintPts,
    ...constraintSegments.map((segment) => [segment[0], segment[1]] as Point),
    ...constraintSegments.map((segment) => [segment[2], segment[3]] as Point),
  ];

  const { segmentIsectParams, vertices } =
    snapRoundPrecision === undefined
      ? intersectSegments(allSegments, segmentToPolygon, constraintSegments, updatedIntsPts)
      : intersectSegmentsSnapRound(
          allSegments,
          segmentToPolygon,
          snapRoundPrecision,
          constraintSegments,
          updatedIntsPts,
        );

  const {
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
    halfEdgeToPolygon,
    polygonToHalfEdges,
    segmentToHalfEdges,
  } = buildHalfEdges(segmentIsectParams, vertices, segmentToPolygon, preventPolygonSelfOverlaps);

  const { faceHalfEdges, halfEdgeToFace, faceSignedAreas } = buildFaces(
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
    vertices,
  );

  const { faceToEdgePolygons, oppositeHalfEdge } = propagatePolygonEdgeAssociations(
    halfEdges,
    halfEdgeToPolygon,
    faceHalfEdges,
    polygonToHalfEdges,
    halfEdgeToFace,
  );

  const { faceToPolygons, faceToParentFace, faceToChildFaces } = findChildFaces(
    vertices,
    halfEdges,
    faceHalfEdges,
    faceSignedAreas,
    faceToEdgePolygons,
    halfEdgeToFace,
    oppositeHalfEdge,
  );

  return new DCEL(
    vertices,
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
    faceHalfEdges,
    faceSignedAreas,
    halfEdgeToFace,
    halfEdgeToPolygon,
    faceToPolygons,
    faceToParentFace,
    faceToChildFaces,
    segmentToPolygon,
    segmentToPolygonLoop,
    segmentToPolygonLoopEdge,
    segmentToHalfEdges,
  );
}

function intersectSegmentsSnapRound(
  segments: Segment[],
  segmentToPolygon: number[],
  snapRoundPrecision: number,
  constraintSegments: Segment[] = [],
  constraintPts: Point[] = [],
): {
  vertices: Point[];
  segmentIsectParams: SegmentIsectParam[][];
} {
  // Snap round segments (mutates in place)
  for (const segment of segments) {
    segment[0] = Math.round(segment[0] / snapRoundPrecision);
    segment[1] = Math.round(segment[1] / snapRoundPrecision);
    segment[2] = Math.round(segment[2] / snapRoundPrecision);
    segment[3] = Math.round(segment[3] / snapRoundPrecision);
  }

  // Snap round constraintSegments and constraintPts
  const snappedConstraintSegments: Segment[] = [];
  for (const constraintSegment of constraintSegments) {
    const snapped = segmentPool.get();
    snapped[0] = Math.round(constraintSegment[0] / snapRoundPrecision);
    snapped[1] = Math.round(constraintSegment[1] / snapRoundPrecision);
    snapped[2] = Math.round(constraintSegment[2] / snapRoundPrecision);
    snapped[3] = Math.round(constraintSegment[3] / snapRoundPrecision);
    snappedConstraintSegments.push(snapped);
  }

  const snappedConstraintPts: Point[] = [];
  for (const constraintPt of constraintPts) {
    snappedConstraintPts.push([
      Math.round(constraintPt[0] / snapRoundPrecision),
      Math.round(constraintPt[1] / snapRoundPrecision),
    ]);
  }

  // Handle constraintSegments and constraintPts via buildSnapRoundSegments
  // This processes them consistently with intersectSegments:
  // - constraintSegments: intersected with segments to create intersection points
  // - constraintPts: added as hot pixels and inserted into segments that contain them
  const { segmentIsectParams, vertices } = buildSnapRoundSegments(
    segments,
    segmentToPolygon,
    snappedConstraintSegments,
    snappedConstraintPts,
  );

  // Scale back to original coordinates
  for (const vertex of vertices) {
    vertex[0] *= snapRoundPrecision;
    vertex[1] *= snapRoundPrecision;
  }

  return { vertices, segmentIsectParams };
}

function propagatePolygonEdgeAssociations(
  halfEdges: HalfEdge[],
  halfEdgeToPolygon: number[][],
  faceHalfEdges: number[][],
  polygonToHalfEdges: Set<number>[],
  halfEdgeToFace: number[],
) {
  const oppositeHalfEdge = halfEdges.map((_, i) => oppositeHalfEdgeIndex(i));

  // half edge pairs cannot both be associated with the same poly
  for (let i = 0; i < halfEdges.length; i++) {
    const opp = oppositeHalfEdge[i];

    const polys = halfEdgeToPolygon[i];
    const oppPolys = halfEdgeToPolygon[opp];

    const duplicatePolys: number[] = [];
    for (const poly of polys) {
      if (oppPolys.includes(poly)) {
        duplicatePolys.push(poly);
      }
    }

    for (const poly of duplicatePolys) {
      remove(polys, poly);
      remove(oppPolys, poly);
    }
  }

  // for each polygon, find all of the faces directly inside it
  const faceToEdgePolygons: Set<number>[] = [];
  for (let i = 0; i < faceHalfEdges.length; i++) {
    faceToEdgePolygons.push(new Set());
  }

  {
    const facesToVisit: number[] = [];
    const visited = new Set<number>();

    for (let polygonId = 0; polygonId < polygonToHalfEdges.length; polygonId++) {
      // get all of the half edges on the polygons perimeter
      const isPerimeterHalfEdge = polygonToHalfEdges[polygonId];

      // DFS of all of the faces directly on the edge of the polygon
      visited.clear();

      // fill the list of faces to visit
      for (const halfedgeIndex of isPerimeterHalfEdge) {
        const face = halfEdgeToFace[halfedgeIndex];
        if (!facesToVisit.includes(face)) {
          facesToVisit.push(face);
        }
      }

      // for each face, add it to the set and add adjacent faces to visit
      while (facesToVisit.length > 0) {
        const faceId = ensureDefined(facesToVisit.pop());
        if (visited.has(faceId)) {
          continue;
        }

        visited.add(faceId);
        faceToEdgePolygons[faceId].add(polygonId);

        for (const currHalfEdgeId of faceHalfEdges[faceId]) {
          // if the half edge is not a perimeter edge of the contour, then add the opposite face
          // to the list of faces to visit
          if (!isPerimeterHalfEdge.has(currHalfEdgeId)) {
            const oppHalfEdgeId = oppositeHalfEdge[currHalfEdgeId];
            const oppFaceId = halfEdgeToFace[oppHalfEdgeId];
            if (!visited.has(oppFaceId)) {
              facesToVisit.push(oppFaceId);
            }
          }
        }
      }
    }
  }

  return { faceToEdgePolygons, oppositeHalfEdge };
}

function findChildFaces(
  vertices: Point[],
  halfEdges: HalfEdge[],
  faceHalfEdges: number[][],
  faceSignedAreas: number[],
  faceToEdgePolygons: Set<number>[],
  halfEdgeToFace: number[],
  oppositeHalfEdge: number[],
): {
  faceToPolygons: Set<number>[];
  faceToParentFace: number[];
  faceToChildFaces: number[][];
} {
  function halfEdgeListToPolygon(inputHalfEdges: number[]) {
    const pts: Point[] = [];

    for (const halfEdge of inputHalfEdges) {
      pts.push(vertices[halfEdges[halfEdge][0]]);
    }

    return pts;
  }

  const isInteriorFace = faceHalfEdges.map((_, faceIdx) => faceSignedAreas[faceIdx] > 0);

  const interiorFacesSortedByArea: number[] = [];

  for (let faceId = 0; faceId < faceHalfEdges.length; faceId++) {
    if (isInteriorFace[faceId]) {
      interiorFacesSortedByArea.push(faceId);
    }
  }

  interiorFacesSortedByArea.sort((a, b) => faceSignedAreas[a] - faceSignedAreas[b]);

  const faceToPolygons: Set<number>[] = [];
  const faceToParentFace: number[] = [];

  // for each exterior face, determine its child/hole faces, by
  // point in polygon checks.
  for (let j = 0; j < faceHalfEdges.length; j++) {
    // an exterior face (CW) is the only kind of face
    // that can be a child of another face
    if (isInteriorFace[j]) {
      continue;
    }

    // get the area of the query face. we will be checking if it is
    // inside another face
    const faceArea = Math.abs(faceSignedAreas[j]);
    const firstVertex = vertices[halfEdges[faceHalfEdges[j][0]][0]];

    // for each interior face, check if the query face is inside it
    for (const interiorFaceId of interiorFacesSortedByArea) {
      // A child face must have a strictly smaller area than a parent
      if (Math.abs(faceSignedAreas[interiorFaceId]) <= faceArea) {
        continue;
      }

      // Child faces are disjoint, don't share an opposite half edge with the parent face
      const oppHalfEdge = oppositeHalfEdgeIndex(faceHalfEdges[interiorFaceId][0]);
      if (halfEdgeToFace[oppHalfEdge] === j) {
        continue;
      }

      // this allocates a lot of memory unnecessarily. Instead, we should have a function
      // to determine containment by simply looking up the vertices of the face w/o
      // allocating.
      const polygon = halfEdgeListToPolygon(faceHalfEdges[interiorFaceId]);

      if (pointInPolygon(firstVertex, polygon)) {
        faceToParentFace[j] = interiorFaceId;
        break;
      }
    }
  }

  // Construct the reverse map from face to child faces from faceToParentFace

  const faceToChildFaces: number[][] = [];

  for (let faceId = 0; faceId < faceHalfEdges.length; faceId++) {
    faceToChildFaces[faceId] = [];
  }

  for (let childId = 0; childId < faceToParentFace.length; childId++) {
    if (faceToParentFace[childId] === undefined) {
      continue;
    }

    const parentId = faceToParentFace[childId];
    faceToChildFaces[parentId].push(childId);
  }

  // for each face
  // get child faces
  // get face to edge polygon
  // while you still have faces to visit, assign face to polygons
  for (let faceId = 0; faceId < faceHalfEdges.length; faceId++) {
    const set = new Set<number>();
    faceToPolygons[faceId] = set;

    if (faceToEdgePolygons[faceId]) {
      for (const polygon of faceToEdgePolygons[faceId]) {
        set.add(polygon);
      }
    }
  }

  const visitedFaces = new Set<number>();

  function applyPolygonsToChildFace(faceId: number, polygons: Set<number>) {
    if (visitedFaces.has(faceId)) {
      return;
    }

    visitedFaces.add(faceId);

    const currFaceToPolygons = faceToPolygons[faceId];
    const currFaceToEdgePolygons = faceToEdgePolygons[faceId];

    if (isInteriorFace[faceId]) {
      // get the list of all polygons containing this face
      const newPolygons = setAdd(polygons, currFaceToEdgePolygons);

      // assign polygons to face
      setAddMutate(currFaceToPolygons, newPolygons);

      // get the child faces and recurse
      for (const childFace of faceToChildFaces[faceId]) {
        applyPolygonsToChildFace(childFace, newPolygons);
      }
    } else {
      // assign polygons to face
      setAddMutate(currFaceToPolygons, polygons);

      // subtract the faceToEdgePolygons from polygons
      const newPolygons = setSubtract(polygons, currFaceToEdgePolygons);

      // recurse on the interior faces
      for (let i = 0; i < faceHalfEdges[faceId].length; i++) {
        applyPolygonsToChildFace(halfEdgeToFace[oppositeHalfEdge[faceHalfEdges[faceId][i]]], newPolygons);
      }
    }
  }

  for (let faceId = 0; faceId < faceHalfEdges.length; faceId++) {
    // for each exterior face with no parent
    if (!isInteriorFace[faceId] && faceToParentFace[faceId] === undefined) {
      applyPolygonsToChildFace(faceId, new Set());
    }
  }
  return { faceToPolygons, faceToParentFace, faceToChildFaces };
}

/**
 * Given a set of input polygons, decompose them into segments.
 */
function decomposeIntoSegments(polygons: Polygon[]): {
  /**
   * The segments
   */
  segments: Segment[];
  /**
   * Maps from segment index to polygon index
   */
  segmentToPolygon: number[];
  /**
   * Maps from segment index to polygon loop index
   */
  segmentToPolygonLoop: number[];
  /**
   * Maps from segment index to polygon loop edge index
   */
  segmentToPolygonLoopEdge: number[];
} {
  const segments: Segment[] = [];
  const segmentToPolygon: number[] = [];
  const segmentToPolygonLoop: number[] = [];
  const segmentToPolygonLoopEdge: number[] = [];

  for (let i = 0; i < polygons.length; i++) {
    const polygon = polygons[i];
    let loopIndex = 0;

    for (const loop of polygon) {
      let a = loop[loop.length - 1];

      let edgeIndex = 0;
      for (const b of loop) {
        if (vec2DistSquared(a[0], a[1], b[0], b[1]) < DIST_EPSILON_SQUARED) {
          edgeIndex++;
          continue;
        }

        const segment = segmentPool.get();

        segment[0] = a[0];
        segment[1] = a[1];
        segment[2] = b[0];
        segment[3] = b[1];

        segments.push(segment);

        segmentToPolygon.push(i);
        segmentToPolygonLoop.push(loopIndex);
        segmentToPolygonLoopEdge.push(edgeIndex);
        a = b;
        edgeIndex++;
      }
      loopIndex++;
    }
  }

  return { segments, segmentToPolygon, segmentToPolygonLoop, segmentToPolygonLoopEdge };
}

/**
 * Intersect all of the segments using an rtree, storing the result in the segmentIsectParams array.
 *
 * As a side effect, this function finds all of the unique vertices in the dcel.
 *
 * @param segments - All segments to intersect
 * @param segmentToPolygon - Maps from segment index to polygon index
 * @param constraintSegments - Optional segments that intersect with segments (used to create intersection points)
 * @param constraintPts - Optional points to insert as intersections on segments that contain them
 */
function intersectSegments(
  segments: Segment[],
  segmentToPolygon: number[],
  constraintSegments: Segment[] = [],
  constraintPts: Point[] = [],
): {
  vertices: Point[];
  segmentIsectParams: SegmentIsectParam[][];
} {
  console.assert(segments.length === segmentToPolygon.length);
  // form all of the bounding boxes
  const segmentIsectParams: SegmentIsectParam[][] = [];

  const boxes: SegmentBox[] = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const ax = segment[0];
    const ay = segment[1];
    const bx = segment[2];
    const by = segment[3];

    const box = bboxPool.get();

    box.segmentIndex = i;
    box.minX = Math.min(ax, bx) - DIST_EPSILON;
    box.minY = Math.min(ay, by) - DIST_EPSILON;
    box.maxX = Math.max(ax, bx) + DIST_EPSILON;
    box.maxY = Math.max(ay, by) + DIST_EPSILON;
    box.length = vec2Dist(ax, ay, bx, by);

    boxes.push(box);

    segmentIsectParams.push([]);
  }

  segmentRTree.clear();
  segmentRTree.load(boxes);

  // keeps track of already intersected pairs
  const intersectedPairs = new Set<number>();

  // the vertices of the dcel
  const vertices: Point[] = [];

  // intersect all segment pairs and form the vertex list
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const ax = segment[0];
    const ay = segment[1];
    const bx = segment[2];
    const by = segment[3];

    // search for all of the segments that intersect this segment
    const iboxes = segmentRTree.search(boxes[i]);

    for (let ibox = 0; ibox < iboxes.length; ibox++) {
      const { segmentIndex: j } = iboxes[ibox];

      if (i === j) {
        continue;
      }

      const hash = hash2(i, j);
      if (intersectedPairs.has(hash)) {
        continue;
      }

      intersectedPairs.add(hash);

      const segmentj = segments[j];
      const bax = segmentj[0];
      const bay = segmentj[1];
      const bbx = segmentj[2];
      const bby = segmentj[3];

      const proximityTolRelaxFactor = Math.min(boxes[i].length, boxes[j].length) * 10;
      const proximityTol = Math.max(proximityTolRelaxFactor, 1) * DIST_EPSILON_SQUARED;

      const isect = intersectLineSegments2D(ax, ay, bx, by, bax, bay, bbx, bby, proximityTol);

      if (isect) {
        if (isect instanceof CurveCurvePointIntersection) {
          const { x, y } = isect;

          const pt: Point = [x, y];

          // when performing each intersection, unify all of the segment vertices
          const a = tryAddIntersection(segmentIsectParams[i], isect.param0, pt);
          const b = tryAddIntersection(segmentIsectParams[j], isect.param1, pt);

          unifyVertexIndices(vertices, a, b);
        } else {
          const { startx, starty, endx, endy } = isect;

          const startpt: Point = [startx, starty];
          const endpt: Point = [endx, endy];

          // these have the same vertex
          const a1 = tryAddIntersection(segmentIsectParams[i], isect.startParam0, startpt);
          const b1 = tryAddIntersection(segmentIsectParams[j], isect.startParam1, startpt);
          unifyVertexIndices(vertices, a1, b1);

          // these have the same vertex
          const a2 = tryAddIntersection(segmentIsectParams[i], isect.endParam0, endpt);
          const b2 = tryAddIntersection(segmentIsectParams[j], isect.endParam1, endpt);
          unifyVertexIndices(vertices, a2, b2);
        }
      }
    }
  }

  // Handle constraintSegments: intersect them with segments to create intersection points
  // This is consistent with intersectSegmentsSnapRound, which uses buildSnapRoundSegments
  for (const constraintSegment of constraintSegments) {
    const constraintAx = constraintSegment[0];
    const constraintAy = constraintSegment[1];
    const constraintBx = constraintSegment[2];
    const constraintBy = constraintSegment[3];

    // Create a temporary box for the intersection segment to search for overlapping segments
    const constraintBox = bboxPool.get();
    constraintBox.minX = Math.min(constraintAx, constraintBx) - DIST_EPSILON;
    constraintBox.minY = Math.min(constraintAy, constraintBy) - DIST_EPSILON;
    constraintBox.maxX = Math.max(constraintAx, constraintBx) + DIST_EPSILON;
    constraintBox.maxY = Math.max(constraintAy, constraintBy) + DIST_EPSILON;
    constraintBox.length = vec2Dist(constraintAx, constraintAy, constraintBx, constraintBy);

    const overlappingBoxes = segmentRTree.search(constraintBox);

    for (const { segmentIndex: j } of overlappingBoxes) {
      const segment = segments[j];
      const ax = segment[0];
      const ay = segment[1];
      const bx = segment[2];
      const by = segment[3];

      const proximityTolRelaxFactor = Math.min(constraintBox.length, boxes[j].length) * 10;
      const proximityTol = Math.max(proximityTolRelaxFactor, 1) * DIST_EPSILON_SQUARED;

      const isect = intersectLineSegments2D(
        constraintAx,
        constraintAy,
        constraintBx,
        constraintBy,
        ax,
        ay,
        bx,
        by,
        proximityTol,
      );

      if (isect) {
        if (isect instanceof CurveCurvePointIntersection) {
          const { x, y } = isect;
          const pt: Point = [x, y];
          const a = tryAddIntersection(segmentIsectParams[j], isect.param1, pt);
          unifyVertexIndices(vertices, a, a);
        } else {
          const { startx, starty, endx, endy } = isect;
          const startpt: Point = [startx, starty];
          const endpt: Point = [endx, endy];
          const a1 = tryAddIntersection(segmentIsectParams[j], isect.startParam1, startpt);
          const a2 = tryAddIntersection(segmentIsectParams[j], isect.endParam1, endpt);
          unifyVertexIndices(vertices, a1, a1);
          unifyVertexIndices(vertices, a2, a2);
        }
      }
    }

    // Note: bboxPool doesn't need individual release, it's released at the start of dcelFromPolygons
  }

  // Handle constraintPts: find segments that contain these points and add them as intersections
  // This is consistent with intersectSegmentsSnapRound, which adds them via buildSnapRoundSegments
  for (const constraintPt of constraintPts) {
    const px = constraintPt[0];
    const py = constraintPt[1];

    // Find segments that contain this point
    for (let j = 0; j < segments.length; j++) {
      const segment = segments[j];
      const ax = segment[0];
      const ay = segment[1];
      const bx = segment[2];
      const by = segment[3];

      // Calculate parametric position t on the segment
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;

      if (len2 < DIST_EPSILON_SQUARED) {
        continue; // Degenerate segment, skip
      }

      const t = ((px - ax) * dx + (py - ay) * dy) / len2;

      // Check if point is actually on the segment (within epsilon)
      const projX = ax + t * dx;
      const projY = ay + t * dy;
      const dist2 = vec2DistSquared(px, py, projX, projY);

      if (dist2 > DIST_EPSILON_SQUARED) {
        continue; // Point not on segment, skip
      }

      // Skip if point is at segment endpoints (t close to 0 or 1)
      if (t < DIST_EPSILON || t > 1 - DIST_EPSILON) {
        continue;
      }

      // Add intersection point
      const a = tryAddIntersection(segmentIsectParams[j], t, constraintPt);
      unifyVertexIndices(vertices, a, a);
    }
  }

  // sort the segment isect params
  for (const segment of segmentIsectParams) {
    sortParams(segment);
  }

  return {
    vertices,
    segmentIsectParams,
  };
}

/**
 * Constructs the half edges of the DCEL from the segmentIsectParams list
 */
function buildHalfEdges(
  segmentIsectParams: SegmentIsectParam[][],
  vertices: Point[],
  segmentToPolygon: number[],
  preventPolygonSelfOverlaps: boolean = true,
): {
  halfEdges: HalfEdge[];
  vertexHalfEdges: number[][];
  halfEdgeVertexIndices: number[];
  halfEdgeToPolygon: number[][];
  polygonToHalfEdges: Set<number>[];
  segmentToHalfEdges: number[][];
} {
  // fill the vertex half edges array
  const vertexHalfEdges: number[][] = [];
  for (let i = 0; i < vertices.length; i++) {
    vertexHalfEdges.push([]);
  }

  // fill the polygon to half edges array
  const polygonCount = Math.max(...segmentToPolygon) + 1;
  const polygonToHalfEdges: Set<number>[] = [];
  for (let i = 0; i < polygonCount; i++) {
    polygonToHalfEdges.push(new Set());
  }

  // we will fill these in the next loop
  const halfEdgeToPolygon: number[][] = [];
  const halfEdges: HalfEdge[] = [];
  const addedHalfEdges = new Map<number, number>();
  const segmentToHalfEdges: number[][] = [];
  const polygonEdgeHashMap: { [edgeHash: string]: number } = {};

  if (preventPolygonSelfOverlaps) {
    // iterate through the intersections along each segment
    // and hash the edges to ensure we don't insert self-overlapping edges
    // from the same polygon
    for (let i = 0; i < segmentIsectParams.length; i++) {
      const params = segmentIsectParams[i];
      const polygon = segmentToPolygon[i];

      if (params.length === 0) {
        continue;
      }

      // add the start vertex
      let a = params[0].vertexIndex;

      // for each isect, make a vertex and build an edge
      for (let j = 1; j < params.length; j++) {
        const b = params[j].vertexIndex;

        const edgeHash = hash3OrderedIJ(a, b, polygon);

        // this step creates a hashmap of polygons which inturn is storing hashmap of edges
        // the idea here is to simultaneously check for overlapping edges from the same polygon
        // and if found then remove the overlapping edges from the edgehashmap if added previously
        if (polygonEdgeHashMap[edgeHash] === undefined) {
          polygonEdgeHashMap[edgeHash] = 1;
        } else {
          polygonEdgeHashMap[edgeHash]++;
        }
        a = b;
      }
    }
    // Process the ordered intersections along each segment again
    // to generate half-edges. Note that the order we do this is important
    // as it ends up filling the segmentToHalfEdges array in the same order
    // as the sorted segmentIsectParams.
    for (let i = 0; i < segmentIsectParams.length; i++) {
      const params = segmentIsectParams[i];
      const polygon = segmentToPolygon[i];
      segmentToHalfEdges.push([]);

      if (params.length === 0) {
        continue;
      }

      // add the start vertex
      let a = params[0].vertexIndex;

      // for each isect, make a vertex and build an edge
      for (let j = 1; j < params.length; j++) {
        const b = params[j].vertexIndex;

        const shouldCreateEdge = preventPolygonSelfOverlaps
          ? polygonEdgeHashMap[hash3OrderedIJ(a, b, polygon)] === 1
          : true;

        if (shouldCreateEdge) {
          tryAddEdge(
            halfEdges,
            vertexHalfEdges,
            addedHalfEdges,
            halfEdgeToPolygon,
            segmentToHalfEdges,
            polygonToHalfEdges,
            a,
            b,
            i,
            polygon,
          );
        }

        a = b;
      }
    }
  } else {
    for (let i = 0; i < segmentIsectParams.length; i++) {
      const params = segmentIsectParams[i];
      const polygon = segmentToPolygon[i];
      segmentToHalfEdges.push([]);

      if (params.length === 0) {
        continue;
      }

      // add the start vertex
      let a = params[0].vertexIndex;

      // for each isect, make a vertex and build an edge
      for (let j = 1; j < params.length; j++) {
        const b = params[j].vertexIndex;

        tryAddEdge(
          halfEdges,
          vertexHalfEdges,
          addedHalfEdges,
          halfEdgeToPolygon,
          segmentToHalfEdges,
          polygonToHalfEdges,
          a,
          b,
          i,
          polygon,
        );

        a = b;
      }
    }
  }
  const halfEdgeAngles: number[] = [];

  for (let i = 0; i < halfEdges.length; i += 2) {
    const halfEdge = halfEdges[i];
    const angle = pointAngle(vertices[halfEdge[0]], vertices[halfEdge[1]]);
    const oppositeAngle = angle <= 0 ? angle + Math.PI : angle - Math.PI;
    halfEdgeAngles.push(angle, oppositeAngle);
  }

  const halfEdgeVertexIndices: number[] = [];
  const compareAngle = (a: number, b: number) => halfEdgeAngles[b] - halfEdgeAngles[a];

  for (let i = 0; i < vertexHalfEdges.length; i++) {
    const vertexHalfEdgeList = vertexHalfEdges[i];

    // sort vertex half edges by angle. they form a cycle so we can ignore
    // the common case where there are only two.
    if (vertexHalfEdgeList.length > 2) {
      vertexHalfEdgeList.sort(compareAngle);
    }

    for (let j = 0; j < vertexHalfEdgeList.length; j++) {
      halfEdgeVertexIndices[vertexHalfEdgeList[j]] = j;
    }
  }

  return {
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
    halfEdgeToPolygon,
    polygonToHalfEdges,
    segmentToHalfEdges,
  };
}

/**
 * Given the half edges, we can now form the faces of the DCEL.
 */
function buildFaces(
  halfEdges: HalfEdge[],
  vertexHalfEdges: number[][],
  halfEdgeVertexIndices: number[],
  vertices: Point[],
): {
  /**
   * The half edges that make up each face
   */
  faceHalfEdges: number[][];
  /**
   * The face that each half edge belongs to
   */
  halfEdgeToFace: number[];
  /**
   * The signed area of each face
   */
  faceSignedAreas: number[];
} {
  const partialDCEL = {
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
  };

  const visitedHalfEdges = new Set<number>();
  const faceHalfEdges: number[][] = [];
  const faceSignedAreas: number[] = [];
  const halfEdgeToFace: number[] = [];

  for (let start = 0; start < halfEdges.length; start++) {
    if (visitedHalfEdges.has(start)) {
      continue;
    }

    const faceIndex = faceHalfEdges.length;
    const curHalfEdges: number[] = [];
    faceHalfEdges.push(curHalfEdges);

    let curr = start;
    visitedHalfEdges.add(curr);
    curHalfEdges.push(curr);
    halfEdgeToFace[curr] = faceIndex;
    let a = vertices[halfEdges[curr][0]];

    let faceSignedArea = 0;

    while (true) {
      const next = nextHalfEdgeIndex(partialDCEL, curr);

      const b = vertices[halfEdges[next][0]];
      faceSignedArea -= (b[0] - a[0]) * (a[1] + b[1]);

      if (visitedHalfEdges.has(next)) {
        break;
      }

      a = b;
      curr = next;
      visitedHalfEdges.add(curr);
      curHalfEdges.push(curr);
      halfEdgeToFace[curr] = faceIndex;
    }

    faceSignedAreas.push(faceSignedArea);
  }

  return { faceHalfEdges, halfEdgeToFace, faceSignedAreas };
}

function unifyVertexIndices(vertices: Point[], a: SegmentIsectParam, b: SegmentIsectParam) {
  if (a.vertexIndex !== -1) {
    if (b.vertexIndex !== -1) {
      if (a.vertexIndex === b.vertexIndex) {
        return;
      }

      console.assert(
        false,
        `Two vertices were within a tolerance unit of one another, but do not have the same vertex index`,
      );
    } else {
      b.vertexIndex = a.vertexIndex;
    }
  } else if (b.vertexIndex !== -1) {
    a.vertexIndex = b.vertexIndex;
  } else {
    // neither have an index
    a.vertexIndex = vertices.length;
    b.vertexIndex = vertices.length;

    vertices.push(a.pt);
  }
}

function tryAddIntersection(ints: SegmentIsectParam[], t: number, pt: Point): SegmentIsectParam {
  for (const int of ints) {
    if (Math.abs(int.t - t) < 1e-6) {
      return int;
    }
  }

  const newInt = segmentIsectParamPool.get();
  newInt.t = t;
  newInt.pt = pt;
  newInt.vertexIndex = -1;
  ints.push(newInt);
  return newInt;
}

function addHalfEdge(
  halfEdges: [number, number][],
  vertexHalfEdges: number[][],
  addedHalfEdges: Map<number, number>,
  halfEdgeToPolygon: number[][],
  segmentToHalfEdges: number[][],
  polygonToHalfEdges: Set<number>[],
  start: number,
  end: number,
  srcSegment?: number,
  srcPolygon?: number,
) {
  const halfEdge = halfEdges.length;
  vertexHalfEdges[start].push(halfEdge);

  const hash = hash2Unordered(start, end);
  addedHalfEdges.set(hash, halfEdge);

  halfEdges.push([start, end]);
  halfEdgeToPolygon[halfEdge] = [];

  if (srcPolygon !== undefined) {
    halfEdgeToPolygon[halfEdge].push(srcPolygon);
    polygonToHalfEdges[srcPolygon].add(halfEdge);
  }

  if (srcSegment !== undefined) {
    segmentToHalfEdges[srcSegment].push(halfEdge);
  }

  return halfEdge;
}

// helper to add a unique half edge
function tryAddEdge(
  halfEdges: [number, number][],
  vertexHalfEdges: number[][],
  addedHalfEdges: Map<number, number>,
  halfEdgeToPolygon: number[][],
  segmentToHalfEdges: number[][],
  polygonToHalfEdges: Set<number>[],
  start: number,
  end: number,
  srcSegment: number,
  srcPolygon?: number,
) {
  const hash = hash2Unordered(start, end);
  const existingHalfEdge = addedHalfEdges.get(hash);

  // we already created this half edge, add polygon/edge association if necessary
  if (existingHalfEdge !== undefined) {
    if (srcPolygon !== undefined) {
      halfEdgeToPolygon[existingHalfEdge].push(srcPolygon);
      polygonToHalfEdges[srcPolygon].add(existingHalfEdge);
    }

    segmentToHalfEdges[srcSegment].push(existingHalfEdge);
    return;
  }

  addHalfEdge(
    halfEdges,
    vertexHalfEdges,
    addedHalfEdges,
    halfEdgeToPolygon,
    segmentToHalfEdges,
    polygonToHalfEdges,
    start,
    end,
    srcSegment,
    srcPolygon,
  );

  addHalfEdge(
    halfEdges,
    vertexHalfEdges,
    addedHalfEdges,
    halfEdgeToPolygon,
    segmentToHalfEdges,
    polygonToHalfEdges,
    end,
    start,
    // don't pass src segment as it is not aligned with the half edge
    // don't pass the src polygon for similar reasons
  );
}

function sortParams(ints: SegmentIsectParam[]) {
  if (ints.length === 2) {
    if (ints[0].t < ints[1].t) {
      return;
    }

    const t = ints[0];
    ints[0] = ints[1];
    ints[1] = t;
    return;
  }
  ints.sort(segmentIsectParamCompare);
}

function segmentIsectParamCompare(a: SegmentIsectParam, b: SegmentIsectParam): number {
  return a.t - b.t;
}

function setSubtract<T>(a: Set<T>, b: Set<T>): Set<T> {
  const newSet = new Set<T>();

  for (const item of a) {
    if (b.has(item)) {
      continue;
    }
    newSet.add(item);
  }

  return newSet;
}

function setAddMutate<T>(a: Set<T>, b: Set<T>) {
  for (const item of b) {
    a.add(item);
  }
}

function setAdd<T>(a: Set<T>, b: Set<T>): Set<T> {
  const newSet = new Set<T>();

  for (const item of a) {
    newSet.add(item);
  }

  for (const item of b) {
    newSet.add(item);
  }

  return newSet;
}

export function oppositeHalfEdgeIndex(halfEdgeIndex: number) {
  if (halfEdgeIndex % 2 === 0) {
    return halfEdgeIndex + 1;
  } else {
    return halfEdgeIndex - 1;
  }
}

function nextHalfEdgeIndex(
  {
    halfEdges,
    vertexHalfEdges,
    halfEdgeVertexIndices,
  }: Pick<DCEL, "halfEdges" | "vertexHalfEdges" | "halfEdgeVertexIndices">,
  halfEdgeIndex: number,
) {
  const oppositeIndex = oppositeHalfEdgeIndex(halfEdgeIndex);
  const startVertex = halfEdges[oppositeIndex][0];
  const startVertexHalfEdges = vertexHalfEdges[startVertex];
  return startVertexHalfEdges[(halfEdgeVertexIndices[oppositeIndex] + 1) % startVertexHalfEdges.length];
}

function remove<T>(arr: T[], t: T) {
  const index = arr.indexOf(t);
  if (index === -1) {
    return;
  }
  arr.splice(index, 1);
}

function pointAngle([x, y]: Point, [xx, yy]: Point) {
  const [dx, dy] = vec2Sub(xx, yy, x, y);
  return Math.atan2(dy, dx);
}

function hash2(a: number, b: number) {
  if (a > b) {
    const t = a;
    a = b;
    b = t;
  }
  return ((a + b) * (a + b + 1)) / 2 + a;
}

function hash2Unordered(a: number, b: number) {
  return ((a + b) * (a + b + 1)) / 2 + a;
}

function hash3OrderedIJ(i: number, j: number, k: number): number {
  if (i > j) {
    const tmp = i;
    i = j;
    j = tmp;
  }

  // Using Szudzik's function extended to 3 dimensions
  const a = 2 * i;
  const b = 2 * j;
  const c = 2 * k;

  const tmp = ((a + b) * (a + b + 1)) / 2 + b;
  return ((tmp + c) * (tmp + c + 1)) / 2 + c;
}
